import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

// functions
import * as ComponentFunctions from './PracitionerProtocolActivity.Functions';
import { PractitionerProtocolActivityMonth } from './PractitionerProtocolActivityMonth';

// models
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { PractitionerVisit } from '../../../models/Entities/Practitioners/Practitioner';

// styles
import './PractitionerProtocolActivity.Styles.css';

const PractitionerProtocolActivityComponent = (props: ComponentFunctions.Props) => {

    const [filteredProtocolIds, setFilteredProtocolIds] = useState<number[]>([]);

    // 1. Load practitioner
    useEffect(() => {
        if (props.PractitionerId) {
            props.GetPractitionerById(props.PractitionerId);
        }
    }, [props.PractitionerId]);

    // 2. Load protocols when practitioner ready
    const practitionerReady = props.Practitioner !== undefined;
    useEffect(() => {
        if (practitionerReady) {
            props.GetProtocolsByPractitionerId(props.PractitionerId);
        }
    }, [practitionerReady, props.PractitionerId]);

    // 3. Compute filtered protocols (memoized)
    const filteredProtocols = useMemo<Protocol[]>(() => {
        if (!props.Protocols) return [];
        if (props.protocolsIds?.length > 0) {
            const idSet = new Set(props.protocolsIds);
            return props.Protocols.filter((p: Protocol) => idSet.has(p.id));
        }
        return props.Protocols;
    }, [props.Protocols, props.protocolsIds]);

    // 4. Stable string key for protocol ids to avoid effect loops
    const protocolIdsKey = useMemo(
        () => filteredProtocols.map((p) => p.id).sort((a, b) => a - b).join(','),
        [filteredProtocols]
    );

    // 5. Fetch visit counts only when inputs really change
    useEffect(() => {
        if (!protocolIdsKey) return;
        const ids = protocolIdsKey.split(',').map(Number);
        setFilteredProtocolIds(ids);
        props.GetPractitionerProtocolVisitsCount(
            props.PractitionerId,
            ids,
            props.DateFrom,
            props.DateTo
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protocolIdsKey, props.PractitionerId, props.DateFrom, props.DateTo]);

    // 6. Build a flat date→count map ONCE per visits/protocols change.
    //    This replaces the per-cell Redux `connect` lookup (was N×42 subscribers).
    const visitsByDateKey = useMemo<Map<string, number>>(() => {
        const map = new Map<string, number>();
        if (!props.visitis || filteredProtocolIds.length === 0) return map;

        const allowed = new Set(filteredProtocolIds);
        props.visitis.forEach((pv: PractitionerVisit) => {
            if (!allowed.has(pv.protocolId)) return;
            pv.visits?.forEach((v) => {
                if (!v?.date) return;
                const key = moment(v.date).format('YYYY-MM-DD');
                map.set(key, (map.get(key) ?? 0) + 1);
            });
        });
        return map;
    }, [props.visitis, filteredProtocolIds]);

    // 7. Months between dates (memoized)
    const months = useMemo(
        () => ComponentFunctions.getMonthsBetweenDates(props.DateFrom, props.DateTo),
        [props.DateFrom, props.DateTo]
    );

    return (
        <div className="activity-container">
            {months.map((m) => (
                <PractitionerProtocolActivityMonth
                    key={`${m.year}-${m.month}`}
                    month={m.month}
                    year={m.year}
                    visitsByDateKey={visitsByDateKey}
                />
            ))}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerProtocolActivityComponent as any);
